import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginModal from '../components/LoginModal';
import '../styles/pages/InfluencerLandingPage.css';

const API_URL = process.env.REACT_APP_API_URL;

function InfluencerLandingPage() {
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(false);
  const rootRef = useRef(null);
  const [me, setMe] = useState(null); // 로그인 사용자 { role } | null

  // 로그인 상태 확인 (LP는 리다이렉트하지 않고 버튼 동작만 전환)
  useEffect(() => {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 3000);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/user/me`, { credentials: 'include', signal: ac.signal });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.ok) setMe({ role: data.role });
      } catch {
        /* 비로그인/네트워크 — 기본(로그아웃) 뷰 유지 */
      } finally {
        clearTimeout(timer);
      }
    })();
    return () => { clearTimeout(timer); ac.abort(); };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const nav = root.querySelector('#nav');
    const onScroll = () => {
      if (nav) nav.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const io = new IntersectionObserver(
      (es) => {
        es.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    root.querySelectorAll('.reveal').forEach((el) => io.observe(el));

    return () => {
      window.removeEventListener('scroll', onScroll);
      io.disconnect();
    };
  }, []);

  // 로그인 상태면(역할 무관): 로그인 버튼 숨김 + 시작 CTA → 본인 역할 마이비즈
  const isLoggedIn = me?.role === 'marketer' || me?.role === 'influencer';
  const startTo = isLoggedIn ? `/${me.role}/home` : '/influencer/register';

  return (
    <div className="lp-inf" ref={rootRef}>
      <header className="nav" id="nav">
        <div className="wrap nav__in">
          {/* 상단 내비는 폭 제약으로 병기 제외 — 로그인 버튼만 병기 유지 (본문 히어로·CTA는 병기) */}
          <div className="brand" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}><span className="brand__dot"></span>셀프애드<span className="brand__tag">인플루언서</span></div>
          <nav className="nav__links">
            <a href="#how">활동 방식</a>
            <a href="#feature">혜택</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="nav__spacer"></div>
          {!isLoggedIn && <button className="btn btn--sm btn--ghost" onClick={() => setLoginOpen(true)}>로그인 (Log in)</button>}
          <button className="btn btn--sm btn--primary" onClick={() => navigate(startTo)}>인플루언서로 시작하기</button>
        </div>
      </header>

      {/* HERO */}
      <section className="hero">
        <div className="blob blob--1"></div>
        <div className="blob blob--2"></div>
        <div className="wrap hero__grid">
          <div className="hero__copy">
            <span className="eyebrow reveal"><b></b>인플루언서를 위한 활동 공간 (A workspace for influencers)</span>
            <h1 className="hero__title reveal" data-d="1">좋아하는 걸 올리고,<br /><span className="accent">활동한 만큼</span> 보상받으세요<span style={{ display: 'block', fontSize: '0.42em', fontWeight: 600, color: '#6b7686', marginTop: '10px' }}>(Post what you love, get rewarded for your activity)</span></h1>
            <p className="hero__sub reveal" data-d="2">팔로워가 적어도 괜찮아요. 오히려 나노·마이크로가 환영받아요. 나와 맞는 프로젝트에 참여하고, 쌓인 보상을 환전하세요. <span style={{ color: '#8b95a5' }}>(Small accounts are welcome — join projects that fit you and withdraw your rewards.)</span></p>
            <div className="hero__cta reveal" data-d="3">
              <button className="btn btn--lg btn--primary" onClick={() => navigate(startTo)}>인플루언서로 시작하기 (Start as Influencer)</button>
              <button className="btn btn--lg btn--ghost" onClick={() => rootRef.current?.querySelector('#how')?.scrollIntoView({ behavior: 'smooth' })}>활동 방식 보기 (How it works)</button>
            </div>
            <p className="hero__note reveal" data-d="3">가입 무료 · 나노·마이크로(1만 이하) 환영 (Free to join · nano/micro welcome)</p>
          </div>
          <div className="hero__media reveal" data-d="2">
            <div className="phone">
              <div className="phone__screen">
                <div className="phone__bar"><div className="phone__notch"></div></div>
                <div className="scr">
                  <div className="scr__top"><span className="scr__title">내 활동 (My Activity)</span><span className="pill pill--mint">레벨 3 (Level 3)</span></div>
                  <div className="reward" style={{ marginBottom: '14px' }}>
                    <div className="reward__label">총 적립 보상 (Total rewards)</div>
                    <div className="reward__amt">₩1,284,000</div>
                    <div className="reward__sub">이번 주 +₩96,000 · 환전 가능 (Withdrawable)</div>
                  </div>
                  <div className="pcard"><div className="pcard__row"><div className="pcard__logo" style={{ background: '#0fb37a' }}>✓</div><div className="pcard__meta"><div className="pcard__name">코스메틱 체험단 (Cosmetics review)</div><div className="pcard__sub">승인 완료 · 릴스 (Approved · Reels)</div></div><div className="pcard__amt" style={{ color: '#0a8a5e' }}>+120,000</div></div></div>
                  <div className="pcard"><div className="pcard__row"><div className="pcard__logo" style={{ background: '#f59e0b' }}>…</div><div className="pcard__meta"><div className="pcard__name">카페 방문 리뷰 (Cafe visit review)</div><div className="pcard__sub">검수 중 · 블로그 (In review · Blog)</div></div><div className="pcard__amt" style={{ color: '#6b7686' }}>대기 (Pending)</div></div></div>
                  <div className="chips"><span className="chip">환전 신청 (Withdraw)</span><span className="chip">새 프로젝트 12 (New: 12)</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FACTS */}
      <div className="wrap">
        <div className="stats reveal">
          <div className="stats__grid">
            <div><div className="stat__num">1만<span className="u">↓</span></div><div className="stat__label">나노·마이크로 환영</div></div>
            <div><div className="stat__num">90<span className="u">%</span></div><div className="stat__label">환전 지급 · 10%는 세금·수수료</div></div>
            <div><div className="stat__num">1~3<span className="u">영업일</span></div><div className="stat__label">환전 통장 입금</div></div>
            <div><div className="stat__num">4<span className="u">개</span></div><div className="stat__label">활동 SNS · 인스타·유튜브·블로그·틱톡</div></div>
          </div>
        </div>
      </div>

      {/* FEATURE 1: 참여 */}
      <section className="block" id="feature">
        <div className="wrap feat">
          <div className="feat__copy reveal">
            <div className="kicker">STEP 01 · 프로젝트 참여</div>
            <h2 className="h2">관심 있는 프로젝트를<br />직접 골라 참여하세요</h2>
            <p className="lead">뷰티·푸드·일상 등 원하는 분야의 프로젝트를 둘러보고, 마음에 드는 프로젝트에 바로 신청할 수 있어요.</p>
            <ul className="feat__list">
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span><div><div className="feat__li-t">나노·마이크로가 오히려 유리</div><div className="feat__li-d">1만 팔로워 이하가 선정 확률이 높아요. 진정성 있는 작은 채널을 광고주가 선호합니다.</div></div></li>
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h16M4 18h10"/></svg></span><div><div className="feat__li-t">분야별로 골라보기</div><div className="feat__li-d">관심 분야만 필터링해 나와 결이 맞는 프로젝트를 찾을 수 있어요.</div></div></li>
            </ul>
            <button className="btn btn--lg btn--primary" onClick={() => navigate(startTo)}>프로젝트 둘러보기 (Browse Projects)</button>
          </div>
          <div className="feat__media reveal" data-d="1">
            <div className="phone">
              <div className="phone__screen">
                <div className="phone__bar"><div className="phone__notch"></div></div>
                <div className="scr">
                  <div className="scr__top"><span className="scr__title">모집 중 프로젝트</span><span className="pill pill--mint">12건</span></div>
                  <div className="pcard"><div className="pcard__row"><div className="pcard__logo" style={{ background: '#2f6bff' }}>B</div><div className="pcard__meta"><div className="pcard__name">신상 코스메틱 체험단</div><div className="pcard__sub">인스타그램 · 릴스</div></div><div className="pcard__amt" style={{ color: '#0a8a5e' }}>₩120,000</div></div></div>
                  <div className="pcard"><div className="pcard__row"><div className="pcard__logo" style={{ background: '#0fb37a' }}>C</div><div className="pcard__meta"><div className="pcard__name">로컬 카페 방문 리뷰</div><div className="pcard__sub">블로그 · 네이버</div></div><div className="pcard__amt" style={{ color: '#0a8a5e' }}>₩80,000</div></div></div>
                  <div className="pcard"><div className="pcard__row"><div className="pcard__logo" style={{ background: '#8b5cf6' }}>D</div><div className="pcard__meta"><div className="pcard__name">신제품 언박싱</div><div className="pcard__sub">유튜브 · 쇼츠</div></div><div className="pcard__amt" style={{ color: '#0a8a5e' }}>₩150,000</div></div></div>
                  <div className="chips"><span className="chip">#뷰티</span><span className="chip">#푸드</span><span className="chip">#일상</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE 2: 보상 적립 */}
      <section className="block" style={{ background: 'var(--bg-soft)', borderRadius: '40px' }}>
        <div className="wrap feat feat--rev">
          <div className="feat__copy reveal">
            <div className="kicker">STEP 02 · 보상 적립</div>
            <h2 className="h2">활동이 승인되면<br />보상이 바로 쌓여요</h2>
            <p className="lead">콘텐츠가 검수를 통과하면 보상이 즉시 적립됩니다. 꾸준히 활동할수록 레벨이 오르고 더 많은 기회가 열려요.</p>
            <ul className="feat__list">
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 15 9l7 .5-5.5 4.5L18 21l-6-4-6 4 1.5-7L2 9.5 9 9z"/></svg></span><div><div className="feat__li-t">승인 즉시 보상 적립</div><div className="feat__li-d">활동한 만큼 투명하게. 적립 내역을 언제든 확인할 수 있어요.</div></div></li>
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20v-6M12 20V4M20 20v-10"/></svg></span><div><div className="feat__li-t">레벨업으로 열리는 기회</div><div className="feat__li-d">활동이 쌓이면 레벨이 오르고, 더 다양한 프로젝트에 참여할 수 있어요.</div></div></li>
            </ul>
          </div>
          <div className="feat__media reveal" data-d="1">
            <div className="phone" style={{ width: '280px' }}>
              <div className="phone__screen">
                <div className="phone__bar"><div className="phone__notch"></div></div>
                <div className="scr">
                  <div className="scr__top"><span className="scr__title">보상 내역</span><span className="pill pill--mint">이번 달</span></div>
                  <div className="reward" style={{ marginBottom: '14px' }}><div className="reward__label">이번 달 적립</div><div className="reward__amt">₩342,500</div><div className="reward__sub">지난 달 대비 +18%</div></div>
                  <div className="pcard"><div className="pcard__row"><div className="pcard__logo" style={{ background: '#0fb37a' }}>✓</div><div className="pcard__meta"><div className="pcard__name">코스메틱 체험단</div><div className="pcard__sub">승인 · 5/12</div></div><div className="pcard__amt" style={{ color: '#0a8a5e' }}>+120,000</div></div></div>
                  <div className="pcard"><div className="pcard__row"><div className="pcard__logo" style={{ background: '#0fb37a' }}>✓</div><div className="pcard__meta"><div className="pcard__name">카페 방문 리뷰</div><div className="pcard__sub">승인 · 5/8</div></div><div className="pcard__amt" style={{ color: '#0a8a5e' }}>+80,000</div></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE 3: 환전 */}
      <section className="block">
        <div className="wrap feat">
          <div className="feat__copy reveal">
            <div className="kicker">STEP 03 · 환전</div>
            <h2 className="h2">쌓인 보상,<br />원할 때 간편하게 환전</h2>
            <p className="lead">적립된 보상은 최소 5,000pt부터 환전 신청할 수 있어요. 영업일 1~3일이면 내 계좌로 정산됩니다. (90% 지급, 10%는 세금·수수료)</p>
            <ul className="feat__list">
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h18v10H3zM3 11h18M7 15h4"/></svg></span><div><div className="feat__li-t">원하는 때 환전 신청</div><div className="feat__li-d">최소 5,000pt부터 언제든 신청할 수 있어요. (한 번에 1건씩)</div></div></li>
              <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 2M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z"/></svg></span><div><div className="feat__li-t">영업일 1~3일 정산</div><div className="feat__li-d">신청하면 셀프애드팀 검토 후 내 계좌로. 정산 상태도 실시간으로 확인돼요.</div></div></li>
            </ul>
            <button className="btn btn--lg btn--primary" onClick={() => navigate(startTo)}>인플루언서로 시작하기 (Start as Influencer)</button>
          </div>
          <div className="feat__media reveal" data-d="1">
            <div className="phone">
              <div className="phone__screen">
                <div className="phone__bar"><div className="phone__notch"></div></div>
                <div className="scr">
                  <div className="scr__top"><span className="scr__title">환전 신청</span><span className="pill pill--mint">가능</span></div>
                  <div className="reward" style={{ marginBottom: '14px' }}><div className="reward__label">환전 가능 잔액</div><div className="reward__amt">₩1,284,000</div><div className="reward__sub">신청 후 영업일 1~3일 정산</div></div>
                  <div className="pcard"><div className="pcard__row"><div className="pcard__logo" style={{ background: '#0fb37a' }}>₩</div><div className="pcard__meta"><div className="pcard__name">국민은행 ****123</div><div className="pcard__sub">기본 정산 계좌</div></div><div className="pcard__amt" style={{ color: '#0a8a5e' }}>신청</div></div></div>
                  <div className="pcard"><div className="pcard__row"><div className="pcard__logo" style={{ background: '#8b5cf6' }}>✓</div><div className="pcard__meta"><div className="pcard__name">지난 환전</div><div className="pcard__sub">완료 · 5/2</div></div><div className="pcard__amt" style={{ color: '#6b7686' }}>300,000</div></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 맞춤 알림 */}
      <section className="block">
        <div className="wrap">
          <div className="reveal" style={{ background: 'var(--blue-soft)', border: '1px solid #cdeee0', borderRadius: '28px', padding: 'clamp(34px,5vw,52px) clamp(22px,4vw,44px)', textAlign: 'center' }}>
            <div style={{ display: 'inline-block', fontWeight: 700, fontSize: '14px', color: '#0a8a5e', background: '#fff', padding: '8px 16px', borderRadius: '999px', marginBottom: '18px', boxShadow: '0 6px 16px -12px rgba(15,179,122,.6)' }}>🔔 맞춤 알림</div>
            <h2 className="h2" style={{ margin: '0 0 14px' }}>일일이 찾지 마세요,<br />맞는 프로젝트가 뜨면 알려드려요</h2>
            <p className="lead" style={{ maxWidth: '54ch', margin: '0 auto' }}>희망하는 광고 분야·단가만 입력해두면, 딱 맞는 프로젝트가 올라올 때 선택하신 방법으로 알림을 보내드려요. 편하게 이용하세요.</p>
          </div>
        </div>
      </section>

      {/* PROJECT TYPES */}
      <section className="block">
        <div className="wrap">
          <div className="reveal">
            <div className="kicker">참여할 수 있는 4가지 유형</div>
            <h2 className="h2">내 스타일에 맞는<br />프로젝트를 골라보세요</h2>
            <p className="lead">광고주가 올리는 프로젝트는 4가지 방식이에요. 내 SNS·라이프스타일에 맞는 유형을 선택해 지원하세요.</p>
          </div>
          <div className="types">
            <div className="type reveal" data-d="1">
              <div className="type__ico">📦</div>
              <div className="type__t">제품 제공</div>
              <div className="type__eng">delivery</div>
              <div className="type__row"><b>추천 대상</b>제품 후기 잘 만드는 분</div>
              <div className="type__row"><b>활동</b>제품 받고 → 사용 → 콘텐츠</div>
              <div className="type__row"><b>대표 사례</b>화장품, 가전</div>
              <div className="type__cps"><span>CPS 가능</span></div>
            </div>
            <div className="type reveal" data-d="1">
              <div className="type__ico">🛒</div>
              <div className="type__t">제품 미제공</div>
              <div className="type__eng">purchase</div>
              <div className="type__row"><b>추천 대상</b>진짜 구매 후기 가능한 분</div>
              <div className="type__row"><b>활동</b>직접 구매 → 사용 → 콘텐츠 (보상 큼)</div>
              <div className="type__row"><b>대표 사례</b>의류 브랜드, 식품</div>
              <div className="type__cps"><span>CPS 가능</span></div>
            </div>
            <div className="type reveal" data-d="2">
              <div className="type__ico">📍</div>
              <div className="type__t">장소 방문</div>
              <div className="type__eng">visit</div>
              <div className="type__row"><b>추천 대상</b>외출·방문 콘텐츠 좋아하는 분</div>
              <div className="type__row"><b>활동</b>매장 방문 → 체험 → 콘텐츠</div>
              <div className="type__row"><b>대표 사례</b>카페, 헬스장, 미용실</div>
              <div className="type__cps"><span style={{ background: '#eef1f6', color: '#6b7686' }}>CPS 미사용</span></div>
            </div>
            <div className="type reveal" data-d="2">
              <div className="type__ico">🎨</div>
              <div className="type__t">자율 형식</div>
              <div className="type__eng">free</div>
              <div className="type__row"><b>추천 대상</b>자율 콘텐츠 자신 있는 분</div>
              <div className="type__row"><b>활동</b>광고주 가이드대로 콘텐츠</div>
              <div className="type__row"><b>대표 사례</b>게임 신작, 강의, 행사</div>
              <div className="type__cps"><span style={{ background: '#fff5e6', color: '#b4700f' }}>CPS 광고주 자율</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* CPS */}
      <section className="block">
        <div className="wrap">
          <div className="cps reveal">
            <div className="cps__grid">
              <div>
                <div className="kicker">CPS · 성과형 추가 리워드</div>
                <h2 className="h2">콘텐츠가 잘 팔릴수록<br />받는 금액도 늘어나요</h2>
                <p className="lead">CPS는 내 콘텐츠로 실제 구매한 고객 1명당 추가 리워드를 받는 시스템이에요. 핵심은 딱 하나 — 콘텐츠에 <b style={{ color: '#fff' }}>추적 URL을 노출</b>하는 것. 결산일에 유입 고객 수만큼 자동 입금됩니다.</p>
                <ul className="feat__list">
                  <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span><div><div className="feat__li-t">추적 URL만 노출하면 끝</div><div className="feat__li-d">본문·프로필 링크에 전용 URL을 넣으면 유입이 자동 집계돼요.</div></div></li>
                  <li><span className="feat__ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span><div><div className="feat__li-t">결산 누락 걱정 없음</div><div className="feat__li-d">광고주가 안 해도 결산일+7일에 셀프애드팀이 자동 처리합니다.</div></div></li>
                </ul>
              </div>
              <div className="cps__ex">
                <div className="cps__ex-t">받을 수 있는 금액 예시 · CPS 5건 유입 시</div>
                <div className="cps__line"><div><div className="cps__nm">A 프로젝트</div><div className="cps__sub">기본 50,000pt + CPS 25,000pt</div></div><div className="cps__amt">75,000<span className="plus">pt</span></div></div>
                <div className="cps__line"><div><div className="cps__nm">B 프로젝트</div><div className="cps__sub">기본 100,000pt + CPS 50,000pt</div></div><div className="cps__amt">150,000<span className="plus">pt</span></div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="block" id="how" style={{ background: 'var(--bg-soft)', borderRadius: '40px' }}>
        <div className="wrap">
          <div className="reveal" style={{ textAlign: 'center' }}>
            <div className="kicker" style={{ textAlign: 'center' }}>간단한 3단계</div>
            <h2 className="h2">가입하고 바로 시작하세요</h2>
            <p className="lead" style={{ margin: '0 auto' }}>복잡한 절차 없이, 관심 있는 프로젝트부터.</p>
          </div>
          <div className="steps">
            <div className="step reveal" data-d="1"><span className="step__ic"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M19 8v6M22 11h-6"/></svg></span><span className="step__n">STEP 01</span><div className="step__t">가입 & SNS 검증</div><p className="step__d">가입 신청 후 영업일 1일 이내 SNS 소유·팔로워 검증을 거쳐 승인돼요. 나노·마이크로 누구나 환영.</p></div>
            <div className="step reveal" data-d="2"><span className="step__ic"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span><span className="step__n">STEP 02</span><div className="step__t">프로젝트 참여</div><p className="step__d">관심 있는 프로젝트에 신청하고, 승인되면 콘텐츠를 제작해 제출합니다.</p></div>
            <div className="step reveal" data-d="3"><span className="step__ic"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></span><span className="step__n">STEP 03</span><div className="step__t">보상 & 환전</div><p className="step__d">활동이 승인되면 보상이 적립되고, 원할 때 간편하게 환전할 수 있어요.</p></div>
          </div>
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="block" id="faq">
        <div className="wrap">
          <div className="faqt reveal">
            <div><h3>가입·활동·환전이 궁금하신가요?</h3><p>인플루언서가 자주 묻는 질문을 모두 정리했어요.</p></div>
            <button className="btn btn--lg btn--ghost" style={{ background: '#fff' }} onClick={() => navigate('/faq')}>FAQ 보러가기 (View FAQ)</button>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="cta">
        <div className="wrap reveal">
          <div className="kicker" style={{ textAlign: 'center' }}>지금 시작하세요</div>
          <h2 className="h2">좋아하는 걸 올리고<br />보상받는 즐거움</h2>
          <p className="lead">가입은 무료예요. 인스타·틱톡 팔로워 1,000명, 유튜브 구독자 1,000명, 네이버 블로그 투데이 20부터 활동할 수 있어요.</p>
          <div className="cta__btns"><button className="btn btn--lg btn--primary" onClick={() => navigate(startTo)}>인플루언서로 시작하기 (Start as Influencer)</button></div>
          <p className="cta__switch">광고주이신가요? <a href="#" onClick={(e) => { e.preventDefault(); navigate('/lp/marketer'); }}>광고주 페이지 (Advertiser page) →</a></p>
        </div>
      </section>

      <footer>
        <div className="wrap">
          <div className="foot__top">
            <div className="brand"><span className="brand__dot"></span>셀프애드</div>
            <nav className="foot__links"><a href="#" onClick={(e) => { e.preventDefault(); navigate('/guide'); }}>서비스 가이드</a><a href="#" onClick={(e) => { e.preventDefault(); navigate('/notice'); }}>공지사항</a><a href="#" onClick={(e) => { e.preventDefault(); navigate('/faq'); }}>FAQ</a><a href="#" onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>이용약관</a><a href="#" onClick={(e) => { e.preventDefault(); navigate('/privacy-policy'); }}>개인정보처리방침</a><a href="#" onClick={(e) => { e.preventDefault(); navigate('/refund-policy'); }}>환불정책</a></nav>
          </div>
          <div className="foot__info"><b>주식회사 카보 (CABO Corp.)</b><br />대표 이동진 · 사업자등록번호 291-86-02840<br />충청남도 계룡시 장안1길 6, 202호 · 고객센터 044-715-7442 · www.selfad.co.kr<br /><span style={{ display: 'inline-block', marginTop: '12px' }}>© 2026 CABO Corp. All rights reserved.</span></div>
        </div>
      </footer>

      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} />}
    </div>
  );
}

export default InfluencerLandingPage;
